import { FC, useEffect, useState } from 'react';
import { NudosButton, NudosStaticAlertInComponent, NudosTextInput } from '../../../../components/NudosComponents';
import NudosDatePicker from '../../../../components/NudosComponents/NudosDatePicker';
import { IGeneralData, IMinDetails, IShipmentData, IproductReferenceDataInCountry } from '../../../../types/orders';
import ProductSelector from '../ProductSelector/ProductSelector';
import { IFormDeliveryProcess } from './DeliveryProcessModal';
import { FALLBACK_FLAG_IMAGE_URL } from '../../../../constants/constants';
import './DeliveryProcessModalContent.scss';

interface IDeliveryProcessModalContent {
  saveDataDeliveyProgress: (data: IFormDeliveryProcess, shippingList: IShipmentData[]) => void;
  loadButton: boolean;
  dataTraking?: IShipmentData;
  editData: (data: IFormDeliveryProcess, shippingList: IShipmentData[]) => void;
  isEdited?: boolean;
  organizationName?: string;
  orderId?: number;
  receiverName?: string;
  flag?: string | null;
  shipmentsAvailableToChangeStatus?: IShipmentData[];
  generalData?: IGeneralData;
  atWarehouseStatus?: boolean;
  references?: IproductReferenceDataInCountry[];
  isStockOrder?: boolean;
}

/**
 * Componente que contiene el contenido del modal de proceso de entrega
 */
const DeliveryProcessModalContent: FC<IDeliveryProcessModalContent> = ({
  saveDataDeliveyProgress,
  editData,
  loadButton,
  dataTraking,
  isEdited,
  receiverName = 'Nombre Cliente',
  orderId,
  flag,
  shipmentsAvailableToChangeStatus,
  generalData,
  atWarehouseStatus,
  references,
  isStockOrder
}: IDeliveryProcessModalContent) => {
  const [formDelivery, setFormDelivery] = useState<IFormDeliveryProcess>();
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [listOfShipmentsToChangeStatus, setListOfShipmentsToChangeStatus] = useState<IShipmentData[]>([]);
  const [errorList, setErrorList] = useState<IMinDetails[] | undefined>([]);
  const [hasProductSelectorErrors, setHasProductSelectorErrors] = useState<boolean>(false);

  const countryTimeZone = dataTraking?.country?.timeZone || undefined;

  const isSaveButtonDisabled =
    (dataTraking?.trackingInfo?.currentEstimatedDeliverDate ? !isEdit || !formDelivery?.date : !formDelivery?.date || loadButton) ||
    hasProductSelectorErrors ||
    !listOfShipmentsToChangeStatus?.length;

  const estimatedDeliveryDateHasChanged =
    !!formDelivery?.date &&
    formDelivery?.date !== dataTraking?.trackingInfo?.currentEstimatedDeliverDate &&
    !!dataTraking?.trackingInfo?.currentEstimatedDeliverDate;

  const trackingInformactionAdded =
    !!formDelivery?.link &&
    !!formDelivery?.code &&
    !dataTraking?.trackingInfo?.trackingLink &&
    !dataTraking?.trackingInfo?.trackerNumber;

  const displayEditDeliveryDateAlert = !!isEdit && (!!trackingInformactionAdded || !!estimatedDeliveryDateHasChanged);

  const editDeliveryDateAlertOptions = {
    title: trackingInformactionAdded ? (
      <div className="editDeliveryDateAlert">
        Has agregado la <strong>información de tracking.</strong> El cliente recibirá un correo con los detalles
      </div>
    ) : (
      <div className="editDeliveryDateAlert">
        Has actualizado la <strong>fecha estimada de entrega</strong>. Enviaremos un correo al cliente con los detalles
      </div>
    )
  };

  const dataForm = () => {
    if (!formDelivery) return;
    if (isEdited) return editData(formDelivery, listOfShipmentsToChangeStatus);
    return saveDataDeliveyProgress?.(formDelivery, listOfShipmentsToChangeStatus);
  };

  const startData = () => {
    setFormDelivery({
      ...formDelivery,
      ...{
        code: String(dataTraking?.trackingInfo?.trackerNumber || ''),
        link: String(dataTraking?.trackingInfo?.trackingLink || ''),
        date: dataTraking?.trackingInfo?.currentEstimatedDeliverDate || ''
      }
    });
  };

  useEffect(() => {
    if (dataTraking && dataTraking?.trackingInfo) {
      startData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataTraking]);

  return (
    <div id="newDeliveryProcessModal">
      <div className="containerTitle">
        <span className="firstPartTitle">{`${generalData?.orgData?.organizationName} - Orden  N°${orderId ?? 1}`}</span>
        <span>|</span>
        <span>{`Envío - ${receiverName}`}</span>
        <img src={flag ?? FALLBACK_FLAG_IMAGE_URL} width={14} alt="delivery-modal-flag" />
      </div>

      <div className="sectionsContainer">
        <NudosDatePicker
          label="Fecha limite estimada de entrega*"
          defaultValue={dataTraking?.trackingInfo?.currentEstimatedDeliverDate || ''}
          isFilled={!!formDelivery?.date}
          updateCurrentValueCallback={e => {
            setIsEdit(true);
            setFormDelivery({ ...formDelivery, ...{ date: e } });
          }}
          desiredTimeZone={countryTimeZone}
          customLabel="customLabel"
          componentSize="small"
        />

        <NudosTextInput
          label="Código/guia de rastreo (opcional)"
          isFilled={!!formDelivery?.code}
          placeholder="Escribe el código/guia aquí"
          defaultValue={formDelivery?.code || ''}
          styleLabel="customLabel"
          handleChange={e => {
            setIsEdit(true);
            setFormDelivery({ ...formDelivery, ...{ code: e } });
          }}
          componentSize="small"
        />

        <NudosTextInput
          label="Link para tracking del servicio (opcional)"
          isFilled={!!formDelivery?.link}
          defaultValue={formDelivery?.link || ''}
          placeholder="Pega el link"
          styleLabel="customLabel"
          handleChange={e => {
            setIsEdit(true);
            setFormDelivery({ ...formDelivery, ...{ link: e } });
          }}
          componentSize="large"
        />
      </div>

      {displayEditDeliveryDateAlert && (
        <NudosStaticAlertInComponent alertOptions={editDeliveryDateAlertOptions} color="blue" type="information" />
      )}

      {atWarehouseStatus && (
        <NudosStaticAlertInComponent
          alertOptions={{
            title:
              'Al cambiar el estado, se actualiza el WMS para indicar que la herramienta salió de bodega y va hacia el empleado.'
          }}
          type="warning"
          color="yellow"
          customTitleClass="customTitleAlert"
          hideTitleClass
          customClassname="customNudosStaticAlertInComponentContainer"
        />
      )}

      <ProductSelector
        headerTitle="Selecciona las herramientas que pasan a proceso de entrega"
        shippingChosenByDefault={dataTraking}
        listOfShipmentsAvailableToAdd={shipmentsAvailableToChangeStatus || []}
        setListOfShipmentsToChangeStatus={setListOfShipmentsToChangeStatus}
        errorList={errorList}
        setErrorList={setErrorList}
        serialHasAnyError={hasErrors => setHasProductSelectorErrors(hasErrors)}
        references={references}
        isStockOrder={isStockOrder}
      />

      <div className="containerButton">
        <NudosButton
          isButtonDisabled={isSaveButtonDisabled}
          buttonText="Guardar"
          isButtonLoading={loadButton}
          handleClick={() => dataForm()}
          customWidth="380px"
          customHeight="38px"
        />
      </div>
    </div>
  );
};

export default DeliveryProcessModalContent;
